// Web CRM (Business plane) — Scheduling configuration authoring.
//
// Real-backend ONLY (scheduling backend candidate). NO fake toggles, NO local-only save.
//   read : GET /api/v1/scheduling/config   (book roles)
//   write: PUT /api/v1/scheduling/config   (management: admin/administrator/manager)
//
// The whole SchedulingConfigUpsert is sent on save; on load the real tenant config populates the
// form (or the model's own safe defaults when none exists yet). Route is management-gated; the
// backend independently enforces the role on PUT.

import React, { useCallback, useEffect, useState } from 'react';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useAuth } from '../../../contexts/AuthContext';
import { canReadScheduling, canSetUpSchedulingFromProfile } from '../../../lib/role';
import { getSelectedTenant, ACTIVE_TENANT_HEADER } from '../../../lib/tenantSession';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../hooks/use-toast';
import { auth, initialAuthReady } from '../../../lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Settings, Loader2, Plus, Trash2, Save, Wand2, CalendarIcon } from 'lucide-react';
// Blackout start/end are picked, never typed. These are the app's EXISTING primitives
// (react-day-picker via ui/calendar, ui/popover) and the EXISTING tenant-timezone authority in
// lib/datetime — no second picker library and no second timezone helper.
import { Calendar } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
// Only the granularity helper is needed here now. The local<->UTC converters are deliberately NOT
// imported: the browser performs no authoritative timezone conversion for blackouts — the backend
// does, with its own tzdata (see scheduling_service.resolve_blackout_local_times).
import { timeOptionsForGranularity, formatTimeLabel12h, formatDateLabel } from '../../../lib/datetime';

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// This page predates the shared authedFetch helper and keeps its own token read (see P0-E/F), so it
// must stamp the active-tenant header itself. The VALUE comes from the same tenantSession authority
// every other protected call uses - no second tenant store. Without it the backend cannot tell which
// of a multi-membership person's tenants this request is for.
const _tenantHeaders = () => {
  const active = getSelectedTenant();
  return active ? { [ACTIVE_TENANT_HEADER]: active } : {};
};

const _getToken = async () => {
  // Await Firebase initial-auth readiness so the PUT save doesn't fire with a null/stale token during the
  // auth-restoration window (would 401 or fall back to a stale localStorage token). See P0-E/F.
  try { await initialAuthReady; } catch (e) { /* ignore */ }
  try { if (auth.currentUser) return await auth.currentUser.getIdToken(); } catch (e) { /* fall through */ }
  return localStorage.getItem('crm_access_token') || '';
};

// ISO <-> <input type="datetime-local"> value (local, minute precision).
// isoToLocal / localToIso were REMOVED with the datetime-local inputs. Both read the BROWSER's zone
// (getHours() / new Date(v).toISOString()), which is precisely why a blackout typed as 19:00 did not
// mean 19:00 at the clinic. Tenant-zone conversion now lives in lib/datetime (one authority).

/**
 * Blackout start/end picker — a CALENDAR for the date and a granularity-stepped SELECT for the time.
 *
 * This replaced `<Input type="datetime-local">`, which had two real defects, not merely poor UX:
 *   1. it interpreted the typed value in the OPERATOR'S BROWSER zone (`new Date(v).toISOString()`),
 *      so the same "19:00" persisted a different real instant depending on who was signed in and the
 *      tenant's own timezone was never consulted;
 *   2. an incomplete entry was silently dropped on save by the `filter(b => b.start_ts && b.end_ts)`
 *      below — the PUT returned 200 and the row simply vanished, with no message. That is the data
 *      loss observed on staging 2026-09-08 (config updated_at moved, blackouts stayed []).
 *
 * Times come from the tenant's OWN `slot_granularity_minutes` — the same authority availability is
 * computed on — so the options a person sees are the boundaries the engine actually uses. Nothing is
 * hardcoded to 15.
 */
// Blackout Start/End are TWO controls each - a date and a time - each in its own grid cell. They were
// previously packed side by side inside ONE cell, which overlapped at ordinary desktop widths and made
// it genuinely easy to set the wrong End. `value` is the TENANT-LOCAL wall clock "YYYY-MM-DDTHH:MM"
// the SERVER computed; the browser performs NO timezone conversion.

const LocalDatePick = ({ value, onChange, testid }) => {
  const [date, time] = String(value || '').split('T');
  const [open, setOpen] = React.useState(false);
  const selected = date ? new Date(date + 'T00:00:00') : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" data-testid={testid}
                className="w-full justify-start font-normal h-9 whitespace-nowrap">
          <CalendarIcon className="h-4 w-4 mr-2 shrink-0 opacity-60" />
          {date ? formatDateLabel(date) : 'Pick a date'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => {
            if (!d) return;
            const p = (n) => String(n).padStart(2, '0');
            const iso = d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
            onChange(iso + 'T' + (time || ''));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

const LocalTimePick = ({ value, step, onChange, testid }) => {
  const [date, time] = String(value || '').split('T');
  const options = timeOptionsForGranularity(step);
  return (
    <select
      data-testid={testid}
      className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm"
      value={time || ''}
      onChange={(e) => onChange((date || '') + 'T' + e.target.value)}
    >
      <option value="">--:--</option>
      {/* VALUE stays the canonical 24-hour wall clock the backend expects; only the LABEL is 12-hour,
          because "07:00" on a Business screen cannot be told apart from 7 in the evening. */}
      {options.map((o) => <option key={o} value={o}>{formatTimeLabel12h(o)}</option>)}
    </select>
  );
};

/** F5 recurrence validation, mirroring the backend BlackoutWindow validators so an invalid rule is
 *  refused here rather than surfacing as a 422. Unbounded recurrence is permitted nowhere.
 *
 *  NOTE: this definition was accidentally destroyed by an earlier region-replace in this file while
 *  its four call sites survived, which shipped a ReferenceError on the SAVE path. The regression test
 *  `renders the editor and saves` now renders and saves the real component, so a missing top-level
 *  helper fails the suite instead of reaching an operator. */
const recurrenceError = (b) => {
  if (!b || !b.repeat) return '';
  if (!['daily', 'weekly'].includes(b.repeat)) return 'Repeat must be Daily or Weekly.';
  if (b.repeat === 'weekly' && !(b.repeat_weekdays || []).length) return 'Pick at least one weekday.';
  if (!b.repeat_until) return 'An "Until" date is required for a repeating block.';
  const startDay = String(b.start_local || b.start_ts || '').slice(0, 10);
  if (startDay && String(b.repeat_until).slice(0, 10) < startDay) return '"Until" cannot be before the start date.';
  return '';
};

/** Inline validation. The save path used to drop an invalid window silently; it now cannot be saved.
 *
 *  VALIDATE WHAT THE OPERATOR ACTUALLY EDITS. This checked `start_ts`/`end_ts` — the canonical UTC
 *  instants — while the pickers write `start_local`/`end_local` and null the instant, because the
 *  browser is no longer permitted to decide what instant "19:00 at the clinic" is. Every newly added
 *  window, and every re-timed one, therefore failed with "Start date and time are required." and the
 *  save was blocked with no way forward: the editor was un-enterable. The wall clock is now the
 *  primary source and the instant is the fallback for a row that predates the local fields.
 *
 *  Ordering is compared in the tenant's own wall clock — both sides are fixed-width
 *  "YYYY-MM-DDTHH:MM" in the SAME zone, so a string comparison is exact and needs no tz database.
 *  A mixed row (one side edited, the other still only an instant) is shape-checked here and left to
 *  the backend to order, since comparing a wall clock against an instant would require precisely the
 *  browser-side conversion this whole change removed. */
const WALL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

const blackoutSide = (local, ts) => {
  const l = String(local || '').trim();
  if (l) return { kind: 'local', v: l };
  const t = String(ts || '').trim();
  if (t) return { kind: 'instant', v: t };
  return null;
};

const sideError = (side, label) => {
  if (side.kind === 'instant') {
    return Number.isNaN(new Date(side.v).getTime()) ? `${label} is not a valid date/time.` : '';
  }
  if (WALL.test(side.v)) return '';
  const [d, t] = side.v.split('T');
  if (!d) return `${label} date is required.`;
  if (!t) return `${label} time is required.`;
  return `${label} is not a valid date/time.`;
};

const blackoutError = (b) => {
  if (!b) return '';
  const s = blackoutSide(b.start_local, b.start_ts);
  const e = blackoutSide(b.end_local, b.end_ts);
  if (!s && !e) return '';                                 // a freshly added, untouched row
  if (!s) return 'Start date and time are required.';
  if (!e) return 'End date and time are required.';
  const se = sideError(s, 'Start'); if (se) return se;
  const ee = sideError(e, 'End'); if (ee) return ee;
  if (s.kind === e.kind) {
    const after = s.kind === 'local' ? e.v > s.v : new Date(e.v) > new Date(s.v);
    if (!after) return 'End must be after start.';
  }
  return '';
};

const DEFAULT_CFG = {
  timezone: 'UTC',
  business_hours: {},
  appointment_types: [],
  providers: [],
  slot_granularity_minutes: 15,
  min_notice_minutes: 0,
  max_advance_days: 30,
  auto_confirm_policy: 'off',
  scheduling_mode: 'appointment',
  industry_pack_id: '',
  workflow_template_id: '',
  customer_display_label: '',
  resource_display_label: '',
  blackouts: [],
  cancellation_policy: { allow_customer_cancel: true, min_notice_minutes: 0, fee_applies: false },
  reschedule_policy: { allow_customer_reschedule: true, min_notice_minutes: 0, max_reschedules: null },
  confirmation_policy: { mode: 'manual', expires_after_minutes: null },
  reminder_policy: { enabled: false, offsets_minutes: [], channels: ['in_app', 'push'], schema_version: '1.0' },
  policy_knowledge_refs: [],
  requires_approval: false,
  requires_payment: false,
  requires_document: false,
};

// Coerce a server config doc into the editable form shape (safe defaults for any missing field).
const fromServer = (c) => {
  if (!c) return { ...DEFAULT_CFG };
  const m = { ...DEFAULT_CFG, ...c };
  m.business_hours = c.business_hours || {};
  m.appointment_types = Array.isArray(c.appointment_types) ? c.appointment_types : [];
  m.providers = Array.isArray(c.providers) ? c.providers : [];
  m.blackouts = Array.isArray(c.blackouts) ? c.blackouts.map((b) => ({ ...b })) : [];
  m.cancellation_policy = { ...DEFAULT_CFG.cancellation_policy, ...(c.cancellation_policy || {}) };
  m.reschedule_policy = { ...DEFAULT_CFG.reschedule_policy, ...(c.reschedule_policy || {}) };
  m.confirmation_policy = { ...DEFAULT_CFG.confirmation_policy, ...(c.confirmation_policy || {}) };
  m.reminder_policy = { ...DEFAULT_CFG.reminder_policy, ...(c.reminder_policy || {}) };
  m.reminder_policy.offsets_minutes = Array.isArray(m.reminder_policy.offsets_minutes) ? m.reminder_policy.offsets_minutes : [];
  m.reminder_policy.channels = Array.isArray(m.reminder_policy.channels) ? m.reminder_policy.channels : ['in_app', 'push'];
  m.policy_knowledge_refs = Array.isArray(c.policy_knowledge_refs) ? c.policy_knowledge_refs : [];
  return m;
};

const asInt = (v, d = 0) => { const n = parseInt(v, 10); return Number.isNaN(n) ? d : n; };
const asIntOrNull = (v) => { if (v === '' || v == null) return null; const n = parseInt(v, 10); return Number.isNaN(n) ? null : n; };

const Section = ({ title, children, action }) => (
  <Card className="mb-4">
    <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
      <CardTitle className="text-base">{title}</CardTitle>
      {action}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs text-gray-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const SchedulingConfig = () => {
  const { currentBusiness, businessStaff } = useBusiness();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const brandId = currentBusiness?.brandId || '';
  // Authoritative product model: scheduling is book-role gated (backend SCHED_BOOK_ROLES); IT/Integration
  // and other non-book roles are not permitted. Gate presentation to match — never issue the request,
  // never show a "Failed to load / retry" loop for a predictably-forbidden resource.
  const schedulingAuthorized = canReadScheduling(user?.role);
  const base = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || '';

  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [existed, setExisted] = useState(true);
  const [setupBusy, setSetupBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [remAvail, setRemAvail] = useState(null); // D5 server-authoritative reminder channel availability
  const [profiles, setProfiles] = useState({ industry_packs: [], workflow_templates: [] }); // friendly selector options (no raw IDs)
  // Slice C — read-only EFFECTIVE config (terminology defaults + provenance) from the ONE backend resolver.
  // Display-only: shows the inherited default (from the tenant's Industry Profile / referenced pack) when a
  // field is not explicitly overridden. scheduling_config stays the sole editable authority here.
  const [effective, setEffective] = useState(null);
  const effField = (name) => (effective && effective.fields && effective.fields[name]) || null;
  const inheritedHint = (name, tenantValue) => {
    const ef = effField(name);
    if (!ef || tenantValue) return null;          // shown only when the tenant hasn't set an explicit value
    if (ef.source === 'tenant_config' || ef.value == null || ef.value === '') return null;
    return `Default: ${ef.value}${ef.source === 'industry_pack' || ef.source === 'industry_profile' ? ' (from Industry Profile)' : ''}`;
  };
  // Provider linkage is chosen BY NAME from the CANONICAL tenant roster already loaded by BusinessContext
  // (businessStaff ← GET /billing/staff/by-brand, the app-wide roster authority). No duplicate fetch, no new
  // Scheduling→commercial coupling. The raw internal staff UUID is never rendered.
  const staffOptions = React.useMemo(
    () => (businessStaff || []).map((mrec) => ({ id: mrec.id, name: mrec.name || mrec.email || '', role: mrec.role || '' })),
    [businessStaff],
  );

  const load = useCallback(async () => {
    if (!brandId) return;
    if (!schedulingAuthorized) { setLoading(false); return; } // unauthorized role — never call a forbidden endpoint
    setLoading(true); setError(null);
    try {
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/scheduling/config?business_id=${encodeURIComponent(brandId)}`, {
        headers: { ..._tenantHeaders(), Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${resp.status}`); }
      const data = await resp.json();
      setExisted(!!data.config);
      setCfg(fromServer(data.config));
      setEffective(data.effective_config || null);   // Slice C — read-only effective terminology/defaults
      try {
        const r2 = await fetch(`${base}/api/v1/scheduling/reminders/availability?business_id=${encodeURIComponent(brandId)}`, {
          headers: { ..._tenantHeaders(), Authorization: `Bearer ${token}` },
        });
        if (r2.ok) setRemAvail(await r2.json());
      } catch (e2) { /* availability is best-effort; controls fall back to safe defaults */ }
      try {
        const r3 = await fetch(`${base}/api/v1/scheduling/profiles?business_id=${encodeURIComponent(brandId)}`, {
          headers: { ..._tenantHeaders(), Authorization: `Bearer ${token}` },
        });
        if (r3.ok) setProfiles(await r3.json());
      } catch (e3) { /* selector options are best-effort; the field still shows the stored value */ }
    } catch (err) {
      setError(err.message || 'error');
    } finally {
      setLoading(false);
    }
  }, [base, brandId]);

  useEffect(() => { load(); }, [brandId, schedulingAuthorized]); // eslint reused rule not active in this project

  // Direct-route defense: a role the backend does not permit for scheduling sees a safe, static
  // "not available" state — no request, no retry loop, no raw 403 text.
  if (!schedulingAuthorized) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" /> {t('nav.schedulingSettings')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Scheduling settings are not available for your role.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  // Provider→staff linkage is chosen BY NAME. The raw internal staff UUID is NEVER rendered as a
  // Business-facing field; the canonical id is kept in state and round-tripped to the API on save.
  const staffLabel = (id) => {
    if (!id) return '';
    const mrec = staffOptions.find((s) => s.id === id);
    if (mrec) return mrec.role ? `${mrec.name} (${mrec.role})` : mrec.name;
    return t('schedcfg.staffId'); // unlisted (e.g. legacy) — neutral label, never the UUID
  };
  const renderStaffSelect = (value, onChange) => (
    <select
      className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
      value={staffOptions.some((s) => s.id === value) ? value : (value ? '__unlisted__' : '')}
      onChange={(e) => onChange(e.target.value && e.target.value !== '__unlisted__' ? e.target.value : (e.target.value === '__unlisted__' ? value : null))}
    >
      <option value="">—</option>
      {value && !staffOptions.some((s) => s.id === value) && <option value="__unlisted__">{staffLabel(value)}</option>}
      {staffOptions.map((s) => (
        <option key={s.id} value={s.id}>{s.role ? `${s.name} (${s.role})` : s.name}</option>
      ))}
    </select>
  );

  // D5 reminder-policy helpers (immutable). Only the fixed 72/24/1h offsets and the generic channel set.
  const setRP = (patch) => setCfg((c) => ({ ...c, reminder_policy: { ...c.reminder_policy, ...patch } }));
  const toggleOffset = (o, on) => setCfg((c) => {
    const cur = c.reminder_policy.offsets_minutes || [];
    return { ...c, reminder_policy: { ...c.reminder_policy, offsets_minutes: on ? Array.from(new Set([...cur, o])) : cur.filter((x) => x !== o) } };
  });
  const toggleChannel = (ch, on) => setCfg((c) => {
    const cur = c.reminder_policy.channels || [];
    return { ...c, reminder_policy: { ...c.reminder_policy, channels: on ? Array.from(new Set([...cur, ch])) : cur.filter((x) => x !== ch) } };
  });
  const smsAvailable = !!(remAvail && remAvail.channels && remAvail.channels.sms && remAvail.channels.sms.available);
  const emailAvailable = !!(remAvail && remAvail.channels && remAvail.channels.email && remAvail.channels.email.available);
  const remCapable = !remAvail || remAvail.capability_available !== false; // unknown => allow edit; backend re-enforces
  // OPEN-05 — the truthful remedy for an UNENTITLED tenant is SERVER-DERIVED (the canonical
  // governance acquisition action), never inferred here and never a hardcoded pack name.
  const remAcquisition = (remAvail && remAvail.acquisition_action) || null;

  // ── Initial setup from the tenant's assigned Industry Profile ────────────────────────────────
  // The profile/pack is the INITIAL CONFIG TEMPLATE, not a live authority (D4, commit 6f197ca:
  // "materialize, don't ref"). This calls the canonical first-adoption route, which derives the
  // tenant, its assigned profile, the pack and the entitlement server-side and seeds
  // db.scheduling_config once. Afterwards the stored config is tenant-owned and edited through the
  // normal Save on this page. Re-running is non-destructive: an existing config is never clobbered.
  //
  // Nothing about the appointment types is reconstructed here — re-keying them in the client would
  // mint fresh ids with no pack provenance and drift from the governed profile.
  const setUpFromProfile = async () => {
    setSetupBusy(true);
    try {
      const token = await _getToken();
      // The template is resolved from the tenant's OWN pack binding, never hardcoded.
      const tResp = await fetch(`${base}/api/v1/billing/me/scheduling/templates`, {
        headers: { ..._tenantHeaders(), Authorization: `Bearer ${token}` },
      });
      if (!tResp.ok) throw new Error(t('schedcfg.setup.noTemplate'));
      const tData = await tResp.json();
      const match = (tData.templates || []).find((x) => x.vertical === packId);
      if (!match) throw new Error(t('schedcfg.setup.noTemplate'));
      const resp = await fetch(`${base}/api/v1/billing/me/scheduling/configure`, {
        method: 'POST',
        headers: { ..._tenantHeaders(), 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ template_key: match.key, config: { timezone: cfg.timezone } }),
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(typeof e.detail === 'string' ? e.detail : (e.detail?.message || `HTTP ${resp.status}`));
      }
      toast({ title: t('schedcfg.setup.done') });
      await load();                       // re-read the canonical stored config; never trust the banner
    } catch (err) {
      toast({ title: t('schedcfg.setup.failed'), description: err.message || 'error', variant: 'destructive' });
    } finally {
      setSetupBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        business_id: brandId,
        timezone: cfg.timezone || 'UTC',
        business_hours: cfg.business_hours || {},
        appointment_types: (cfg.appointment_types || []).map((tpe) => ({
          ...(tpe.id ? { id: tpe.id } : {}),
          name: tpe.name || '',
          duration_minutes: asInt(tpe.duration_minutes, 30),
          buffer_minutes: asInt(tpe.buffer_minutes, 0),
          requires_approval: !!tpe.requires_approval,
          requires_payment: !!tpe.requires_payment,
          requires_document: !!tpe.requires_document,
          consultation_mode: tpe.consultation_mode || 'in_person',
          // H3 — pre-visit preparation for THIS type. Sent through the SAME config write authority as
          // every other per-type field; there is no pre-visit endpoint and no pre-visit store. Blank
          // is sent as null so the backend records absence rather than an empty string.
          preparation_instructions: (tpe.preparation_instructions || '').trim() || null,
        })),
        providers: (cfg.providers || []).map((p) => ({
          ...(p.id ? { id: p.id } : {}),
          name: p.name || '',
          staff_id: p.staff_id || null,
          appointment_type_ids: Array.isArray(p.appointment_type_ids) ? p.appointment_type_ids : [],
          resource_type: p.resource_type || 'staff',
          resource_id: p.resource_id || null,
        })),
        slot_granularity_minutes: asInt(cfg.slot_granularity_minutes, 15),
        min_notice_minutes: asInt(cfg.min_notice_minutes, 0),
        max_advance_days: asInt(cfg.max_advance_days, 30),
        auto_confirm_policy: cfg.auto_confirm_policy || 'off',
        scheduling_mode: cfg.scheduling_mode || 'appointment',
        industry_pack_id: cfg.industry_pack_id || null,
        workflow_template_id: cfg.workflow_template_id || null,
        customer_display_label: cfg.customer_display_label || null,
        resource_display_label: cfg.resource_display_label || null,
        blackouts: [], // set below (handles both ISO-from-server and datetime-local-from-edit)
        cancellation_policy: {
          allow_customer_cancel: !!cfg.cancellation_policy.allow_customer_cancel,
          min_notice_minutes: asInt(cfg.cancellation_policy.min_notice_minutes, 0),
          fee_applies: !!cfg.cancellation_policy.fee_applies,
        },
        reschedule_policy: {
          allow_customer_reschedule: !!cfg.reschedule_policy.allow_customer_reschedule,
          min_notice_minutes: asInt(cfg.reschedule_policy.min_notice_minutes, 0),
          max_reschedules: asIntOrNull(cfg.reschedule_policy.max_reschedules),
        },
        confirmation_policy: {
          mode: cfg.confirmation_policy.mode || 'manual',
          expires_after_minutes: asIntOrNull(cfg.confirmation_policy.expires_after_minutes),
        },
        reminder_policy: {
          enabled: !!cfg.reminder_policy.enabled,
          offsets_minutes: (cfg.reminder_policy.offsets_minutes || []).filter((o) => [4320, 1440, 60].includes(o)),
          channels: (cfg.reminder_policy.channels || []).filter((ch) => ['in_app', 'push', 'sms', 'email'].includes(ch)),
          schema_version: cfg.reminder_policy.schema_version || '1.0',
        },
        policy_knowledge_refs: cfg.policy_knowledge_refs || [],
        requires_approval: !!cfg.requires_approval,
        requires_payment: !!cfg.requires_payment,
        requires_document: !!cfg.requires_document,
      };
      // BLACKOUTS. The picker already produces canonical UTC ISO in the TENANT's zone, so there is no
      // conversion left to do here — the old `localToIso` step read the operator's BROWSER zone and
      // silently persisted the wrong instant for anyone not sitting in the tenant's timezone.
      //
      // An invalid window is now REFUSED, not filtered away. The previous
      // `.filter(b => b.start_ts && b.end_ts)` dropped an incomplete row on the way out: the PUT
      // returned 200, the config's updated_at moved, and the blackout simply did not exist. That is
      // silent data loss, and it is exactly what was observed on staging 2026-09-08.
      const _bad = (cfg.blackouts || []).findIndex((b) => blackoutError(b) || recurrenceError(b));
      if (_bad >= 0) {
        toast({
          title: t('schedcfg.blackouts'),
          description: blackoutError(cfg.blackouts[_bad]) || recurrenceError(cfg.blackouts[_bad]),
          variant: 'destructive',
        });
        setSaving(false);
        return;
      }
      payload.blackouts = (cfg.blackouts || [])
        .filter((b) => b.start_local || b.end_local || b.start_ts || b.end_ts)   // drop untouched rows only
        .map((b) => ({
          // Send the tenant-local wall clock. The backend resolves it with ITS tzdata; a stale
          // canonical instant is omitted so the two can never be sent in conflict.
          start_local: b.start_local || undefined,
          end_local: b.end_local || undefined,
          start_ts: b.start_local ? undefined : b.start_ts,
          end_ts: b.end_local ? undefined : b.end_ts,
          reason: b.reason || null,
          provider_staff_id: b.provider_staff_id || null,
          // F5 — omitted entirely when not repeating, so the persisted document is unchanged for a
          // one-time window and older clients keep round-tripping it verbatim.
          repeat: b.repeat || null,
          repeat_weekdays: b.repeat === 'weekly' ? (b.repeat_weekdays || []) : [],
          repeat_until: b.repeat ? (b.repeat_until || null) : null,
        }));

      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/scheduling/config`, {
        method: 'PUT',
        headers: { ..._tenantHeaders(), 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${resp.status}`); }
      toast({ title: t('schedcfg.saved') });
      await load();
    } catch (err) {
      toast({ title: t('schedcfg.saveFailed'), description: err.message || 'error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ---- array helpers (immutable) ----
  const addWindow = (day) => setCfg((c) => {
    const bh = { ...(c.business_hours || {}) };
    bh[day] = [...(bh[day] || []), { start: '09:00', end: '17:00' }];
    return { ...c, business_hours: bh };
  });
  const setWindow = (day, i, key, val) => setCfg((c) => {
    const bh = { ...(c.business_hours || {}) };
    const list = [...(bh[day] || [])];
    list[i] = { ...list[i], [key]: val };
    bh[day] = list;
    return { ...c, business_hours: bh };
  });
  const removeWindow = (day, i) => setCfg((c) => {
    const bh = { ...(c.business_hours || {}) };
    bh[day] = (bh[day] || []).filter((_, idx) => idx !== i);
    if (bh[day].length === 0) delete bh[day];
    return { ...c, business_hours: bh };
  });

  // EFFECTIVE APPOINTMENT-TYPE AUTHORITY.
  //
  // For a tenant pinned to an industry profile/pack, the effective bookable types come from the PACK,
  // not from `scheduling_config.appointment_types` -- precedence is profile override -> pack -> tenant
  // config. Editing the tenant list therefore had NO runtime effect: a Clinic admin could set Follow-up
  // to "video", the field saved, the screen redisplayed "video", and every booking still persisted
  // in_person. Nothing anywhere said so.
  //
  // The backend already publishes the answer -- `effective_config.fields.appointment_types.{value,source}`
  // from the ONE canonical resolver -- so this consumes it rather than adding a second authority or
  // changing precedence. Where the profile owns the value we show the EFFECTIVE list and say who owns it;
  // where the tenant genuinely owns it, editing is untouched.
  const _typesField = effective?.fields?.appointment_types || null;
  const typesSource = _typesField?.source || null;
  const typesProfileControlled = typesSource === 'industry_pack' || typesSource === 'industry_profile';
  const effectiveTypes = typesProfileControlled && Array.isArray(_typesField?.value) ? _typesField.value : null;
  const typesSourceLabel = typesSource === 'industry_pack' ? t('schedcfg.types.srcPack')
    : typesSource === 'industry_profile' ? t('schedcfg.types.srcProfile')
      : typesSource === 'tenant_config' ? t('schedcfg.types.srcTenant') : null;

  const addType = () => setCfg((c) => ({ ...c, appointment_types: [...(c.appointment_types || []), { name: '', duration_minutes: 30, buffer_minutes: 0, requires_approval: false, requires_payment: false, requires_document: false, consultation_mode: 'in_person', preparation_instructions: '' }] }));
  const setType = (i, patch) => setCfg((c) => { const a = [...c.appointment_types]; a[i] = { ...a[i], ...patch }; return { ...c, appointment_types: a }; });
  const removeType = (i) => setCfg((c) => ({ ...c, appointment_types: c.appointment_types.filter((_, idx) => idx !== i) }));

  const addProvider = () => setCfg((c) => ({ ...c, providers: [...(c.providers || []), { name: '', staff_id: '', appointment_type_ids: [], resource_type: 'staff', resource_id: '' }] }));
  const setProvider = (i, patch) => setCfg((c) => { const a = [...c.providers]; a[i] = { ...a[i], ...patch }; return { ...c, providers: a }; });
  const removeProvider = (i) => setCfg((c) => ({ ...c, providers: c.providers.filter((_, idx) => idx !== i) }));

  const addBlackout = () => setCfg((c) => ({ ...c, blackouts: [...(c.blackouts || []), { start_local: '', end_local: '', reason: '', provider_staff_id: '' }] }));
  const setBlackout = (i, patch) => setCfg((c) => { const a = [...c.blackouts]; a[i] = { ...a[i], ...patch }; return { ...c, blackouts: a }; });
  const removeBlackout = (i) => setCfg((c) => ({ ...c, blackouts: c.blackouts.filter((_, idx) => idx !== i) }));

  if (!currentBusiness || loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-purple-600" />
          <p className="text-gray-600">{t('schedcfg.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <Card><CardContent className="pt-6 text-center">
          <p className="text-red-700 mb-2">{t('schedcfg.failedLoad')}</p>
          <p className="text-xs text-gray-500 mb-4 break-all">{error}</p>
          <Button onClick={load}>{t('common.retry')}</Button>
        </CardContent></Card>
      </div>
    );
  }

  // Truthful eligibility, entirely from backend state — never from a profile NAME string:
  //   * the tenant has an assigned profile whose pack can materialize (effective_config provenance)
  //   * no scheduling config exists yet (first adoption only)
  //   * the caller holds the narrower billing-admin authority the configure route enforces
  // Entitlement is enforced server-side; a refusal surfaces as an honest error rather than a hidden
  // control, so the tenant is never told a capability is missing when it is merely unentitled.
  const packId = effective?.referenced_pack_id || null;
  const canSetUp = !!packId && !existed && canSetUpSchedulingFromProfile(user?.role);

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Settings className="h-7 w-7 text-purple-600" />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('page.schedConfig.title')}</h1>
            <p className="text-gray-600">{t('page.schedConfig.subtitle', { business: currentBusiness.businessName || brandId })}</p>
            {!existed && <p className="text-xs text-amber-600 mt-1">{t('schedcfg.none')}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canSetUp && (
            <Button onClick={setUpFromProfile} disabled={setupBusy} variant="outline"
                    data-testid="schedcfg-setup-from-profile">
              {setupBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
              {t('schedcfg.setup.cta')}
            </Button>
          )}
          <Button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {t('schedcfg.save')}
          </Button>
        </div>
      </div>

      {/* General */}
      <Section title={t('schedcfg.general')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label={t('schedcfg.timezone')}><Input value={cfg.timezone} onChange={(e) => set({ timezone: e.target.value })} /></Field>
          <Field label={t('schedcfg.slotGranularity')}><Input type="number" value={cfg.slot_granularity_minutes} onChange={(e) => set({ slot_granularity_minutes: e.target.value })} /></Field>
          <Field label={t('schedcfg.minNotice')}><Input type="number" value={cfg.min_notice_minutes} onChange={(e) => set({ min_notice_minutes: e.target.value })} /></Field>
          <Field label={t('schedcfg.maxAdvance')}><Input type="number" value={cfg.max_advance_days} onChange={(e) => set({ max_advance_days: e.target.value })} /></Field>
          <Field label={t('schedcfg.autoConfirm')}>
            <select className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" value={cfg.auto_confirm_policy} onChange={(e) => set({ auto_confirm_policy: e.target.value })}>
              <option value="off">{t('schedcfg.autoConfirmOff')}</option>
              <option value="rule_based">{t('schedcfg.autoConfirmRule')}</option>
            </select>
          </Field>
          <Field label={t('schedcfg.mode')}><Input value={cfg.scheduling_mode} onChange={(e) => set({ scheduling_mode: e.target.value })} /></Field>
          <Field label={t('schedcfg.customerLabel')}>
            <Input value={cfg.customer_display_label || ''} onChange={(e) => set({ customer_display_label: e.target.value })} placeholder={effField('customer_display_label')?.value || ''} />
            {inheritedHint('customer_display_label', cfg.customer_display_label) && (
              <p className="text-xs text-gray-500 mt-1" data-testid="schedcfg-customerlabel-inherited">{inheritedHint('customer_display_label', cfg.customer_display_label)}</p>
            )}
          </Field>
          <Field label={t('schedcfg.resourceLabel')}>
            <Input value={cfg.resource_display_label || ''} onChange={(e) => set({ resource_display_label: e.target.value })} placeholder={effField('resource_display_label')?.value || ''} />
            {inheritedHint('resource_display_label', cfg.resource_display_label) && (
              <p className="text-xs text-gray-500 mt-1" data-testid="schedcfg-resourcelabel-inherited">{inheritedHint('resource_display_label', cfg.resource_display_label)}</p>
            )}
          </Field>
          <Field label={t('schedcfg.industryPack')}>
            <select className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
              value={cfg.industry_pack_id || ''} onChange={(e) => set({ industry_pack_id: e.target.value })}>
              <option value="">Select…</option>
              {(profiles.industry_packs || []).map((p) => (
                <option key={p.id} value={p.id}>{p.label}{p.version ? ` (v${p.version})` : ''}</option>
              ))}
            </select>
          </Field>
          <Field label={t('schedcfg.workflowTemplate')}>
            <select className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
              value={cfg.workflow_template_id || ''} onChange={(e) => set({ workflow_template_id: e.target.value })}>
              <option value="">Select…</option>
              {(profiles.workflow_templates || []).map((w) => (
                <option key={w.id} value={w.id}>{w.label}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="mt-3 flex flex-wrap gap-4">
          <Chk label={t('sched.req.approval')} v={cfg.requires_approval} on={(v) => set({ requires_approval: v })} />
          <Chk label={t('sched.req.payment')} v={cfg.requires_payment} on={(v) => set({ requires_payment: v })} />
          <Chk label={t('sched.req.document')} v={cfg.requires_document} on={(v) => set({ requires_document: v })} />
        </div>
      </Section>

      {/* D5 — Appointment reminders (72h / 24h / 1h). Availability + capability are server-authoritative. */}
      <Section title={t('schedcfg.reminders.title')}>
        {!remCapable && (
          <p className="text-xs text-amber-600 mb-3">
            {t('schedcfg.reminders.unavailable')}
            {remAcquisition === 'add_on' ? ` ${t('schedcfg.reminders.addOnRemedy')}` : ''}
          </p>
        )}
        <div className={remCapable ? '' : 'opacity-50 pointer-events-none'}>
          <Chk label={t('schedcfg.reminders.enabled')} v={!!cfg.reminder_policy.enabled} on={(v) => setRP({ enabled: v })} />
          <div className="mt-3">
            <div className="text-xs text-gray-500 mb-1">{t('schedcfg.reminders.offsets')}</div>
            <div className="flex flex-wrap gap-4">
              <Chk label={t('schedcfg.reminders.o72')} v={(cfg.reminder_policy.offsets_minutes || []).includes(4320)} on={(v) => toggleOffset(4320, v)} />
              <Chk label={t('schedcfg.reminders.o24')} v={(cfg.reminder_policy.offsets_minutes || []).includes(1440)} on={(v) => toggleOffset(1440, v)} />
              <Chk label={t('schedcfg.reminders.o1')} v={(cfg.reminder_policy.offsets_minutes || []).includes(60)} on={(v) => toggleOffset(60, v)} />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xs text-gray-500 mb-1">{t('schedcfg.reminders.channels')}</div>
            <div className="flex flex-wrap gap-4">
              <Chk label={t('schedcfg.reminders.chInApp')} v={(cfg.reminder_policy.channels || []).includes('in_app')} on={(v) => toggleChannel('in_app', v)} />
              <Chk label={t('schedcfg.reminders.chPush')} v={(cfg.reminder_policy.channels || []).includes('push')} on={(v) => toggleChannel('push', v)} />
              {/* SMS control appears ONLY when the backend reports SMS is available (real provider + tenant-enabled). */}
              {smsAvailable && (
                <Chk label={t('schedcfg.reminders.chSms')} v={(cfg.reminder_policy.channels || []).includes('sms')} on={(v) => toggleChannel('sms', v)} />
              )}
              {emailAvailable && (
                <Chk label={t('schedcfg.reminders.chEmail')} v={(cfg.reminder_policy.channels || []).includes('email')} on={(v) => toggleChannel('email', v)} />
              )}
            </div>
            {!smsAvailable && <p className="text-xs text-gray-400 mt-1">{t('schedcfg.reminders.smsUnavailable')}</p>}
          </div>
        </div>
      </Section>

      {/* Business hours */}
      <Section title={t('schedcfg.hours')}>
        <div className="space-y-2">
          {WEEKDAYS.map((day) => (
            <div key={day} className="flex items-start gap-3">
              <div className="w-28 pt-1.5 text-sm text-gray-700">{t(`schedcfg.day.${day}`)}</div>
              <div className="flex-1 space-y-1.5">
                {(cfg.business_hours[day] || []).length === 0 && <span className="text-xs text-gray-400">{t('schedcfg.closed')}</span>}
                {(cfg.business_hours[day] || []).map((w, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input type="time" className="h-8 w-32" value={w.start || ''} onChange={(e) => setWindow(day, i, 'start', e.target.value)} />
                    <span className="text-gray-400">–</span>
                    <Input type="time" className="h-8 w-32" value={w.end || ''} onChange={(e) => setWindow(day, i, 'end', e.target.value)} />
                    <Button size="sm" variant="ghost" className="text-red-500 h-8 px-2" onClick={() => removeWindow(day, i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button size="sm" variant="ghost" className="h-7 text-purple-600 px-2" onClick={() => addWindow(day)}><Plus className="h-3.5 w-3.5 mr-1" />{t('schedcfg.addWindow')}</Button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Appointment types */}
      <Section title={t('schedcfg.types')}
        action={typesProfileControlled ? null : <Button size="sm" variant="outline" onClick={addType}><Plus className="h-4 w-4 mr-1" />{t('schedcfg.addType')}</Button>}>
        {typesProfileControlled ? (
          <div data-testid="types-profile-controlled">
            <p className="text-xs text-amber-700 mb-3">{t('schedcfg.types.inherited')}{typesSourceLabel ? ` (${typesSourceLabel})` : ''}</p>
            <div className="space-y-2">
              {(effectiveTypes || []).map((tp, i) => (
                <div key={tp.id || i} className="border border-gray-100 rounded-md p-3 bg-gray-50">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                    <span className="text-sm font-medium text-gray-900">{tp.name}</span>
                    <span className="text-xs text-gray-500">{t('schedcfg.duration')}: {tp.duration_minutes}</span>
                    <span className="text-xs text-gray-700">
                      {t('schedcfg.mode.label')}:{' '}
                      <span className="font-medium" data-testid={`eff-mode-${tp.name}`}>
                        {tp.consultation_mode === 'video' ? t('schedcfg.mode.video')
                          : tp.consultation_mode === 'audio' ? t('schedcfg.mode.audio')
                            : t('schedcfg.mode.inPerson')}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (cfg.appointment_types || []).length === 0 ? <p className="text-xs text-gray-400">{t('common.noData')}</p> : (
          <div className="space-y-3">
            {cfg.appointment_types.map((tp, i) => (
              <div key={tp.id || i} className="border border-gray-100 rounded-md p-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Field label={t('schedcfg.typeName')}><Input value={tp.name || ''} onChange={(e) => setType(i, { name: e.target.value })} /></Field>
                  <Field label={t('schedcfg.duration')}><Input type="number" value={tp.duration_minutes} onChange={(e) => setType(i, { duration_minutes: e.target.value })} /></Field>
                  <Field label={t('schedcfg.buffer')}><Input type="number" value={tp.buffer_minutes} onChange={(e) => setType(i, { buffer_minutes: e.target.value })} /></Field>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-4">
                  {/* HOW this type is conducted. Generic and industry-free: a consultation, a legal
                      meeting and a support session are all held in person, over audio or over video.
                      Absent means in person, so every type that predates this keeps behaving as it
                      always has and no tenant has to touch anything. */}
                  <label className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{t('schedcfg.mode.label')}</span>
                    <select
                      className="text-xs border border-gray-200 rounded px-2 py-1"
                      value={tp.consultation_mode || 'in_person'}
                      onChange={(e) => setType(i, { consultation_mode: e.target.value })}
                    >
                      <option value="in_person">{t('schedcfg.mode.inPerson')}</option>
                      <option value="audio">{t('schedcfg.mode.audio')}</option>
                      <option value="video">{t('schedcfg.mode.video')}</option>
                    </select>
                  </label>
                  <Chk label={t('sched.req.approval')} v={tp.requires_approval} on={(v) => setType(i, { requires_approval: v })} />
                  <Chk label={t('sched.req.payment')} v={tp.requires_payment} on={(v) => setType(i, { requires_payment: v })} />
                  <Chk label={t('sched.req.document')} v={tp.requires_document} on={(v) => setType(i, { requires_document: v })} />
                  <Button size="sm" variant="ghost" className="text-red-500 ml-auto" onClick={() => removeType(i)}><Trash2 className="h-4 w-4 mr-1" />{t('schedcfg.remove')}</Button>
                </div>

                {/* H3 — PRE-VISIT PREPARATION. Guidance for everyone who books THIS TYPE: what to
                    bring, when to arrive, how to prepare. It is deliberately per-TYPE and never
                    per-patient — the helper text says so, because a free-text box on a clinical screen
                    is exactly where someone would otherwise be tempted to write about a person. It
                    gates nothing and changes no availability. */}
                <div className="mt-2">
                  <Field label={t('schedcfg.preparation')}>
                    <textarea
                      data-testid={`type-preparation-${i}`}
                      className="w-full min-h-[64px] rounded-md border border-gray-200 px-3 py-2 text-sm"
                      maxLength={2000}
                      value={tp.preparation_instructions || ''}
                      placeholder={t('schedcfg.preparationPlaceholder')}
                      onChange={(e) => setType(i, { preparation_instructions: e.target.value })}
                    />
                  </Field>
                  <p className="text-xs text-gray-400 mt-1" data-testid={`type-preparation-hint-${i}`}>
                    {t('schedcfg.preparationHint')}
                    <span className="ml-2 tabular-nums">
                      {(tp.preparation_instructions || '').length}/2000
                    </span>
                  </p>
                </div>

                {/* The backend has carried these two fields since the document prerequisite shipped,
                    and nothing ever exposed them. A practice could say a document was required but
                    not WHICH one, so patients were told "Required document is required" -- the
                    platform's placeholder, read back as though it were a name. Naming it here is
                    what makes the assistant's answer useful, and it stays the tenant's wording:
                    the platform never assumes a jurisdiction, a document or an industry. */}
                {tp.requires_document && (
                  <div className="mt-2 pl-1 border-l-2 border-gray-100 space-y-2">
                    <Txt
                      label={t('schedcfg.doc.label')}
                      placeholder={t('schedcfg.doc.labelPlaceholder')}
                      v={tp.document_requirement_label || ''}
                      on={(v) => setType(i, { document_requirement_label: v || null })}
                    />
                    <p className="text-[11px] text-gray-400">{t('schedcfg.doc.labelHelp')}</p>
                    <div className="flex flex-wrap items-center gap-4">
                      {/* WHAT may satisfy it. The server validates every upload against these
                          regardless; this decides what the patient is offered, not what is let in. */}
                      <Chk
                        label={t('schedcfg.doc.kindDocument')}
                        v={(tp.document_media_kinds || DEFAULT_DOC_KINDS).includes('document')}
                        on={(v) => setType(i, { document_media_kinds: toggleKind(tp, 'document', v) })}
                      />
                      <Chk
                        label={t('schedcfg.doc.kindImage')}
                        v={(tp.document_media_kinds || DEFAULT_DOC_KINDS).includes('image')}
                        on={(v) => setType(i, { document_media_kinds: toggleKind(tp, 'image', v) })}
                      />
                    </div>
                    {(tp.document_media_kinds || DEFAULT_DOC_KINDS).length === 0 && (
                      <p className="text-[11px] text-red-600">{t('schedcfg.doc.kindsEmpty')}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Providers */}
      <Section title={t('schedcfg.providers')} action={<Button size="sm" variant="outline" onClick={addProvider}><Plus className="h-4 w-4 mr-1" />{t('schedcfg.addProvider')}</Button>}>
        {(cfg.providers || []).length === 0 ? <p className="text-xs text-gray-400">{t('common.noData')}</p> : (
          <div className="space-y-3">
            {cfg.providers.map((p, i) => (
              <div key={p.id || i} className="border border-gray-100 rounded-md p-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Field label={t('schedcfg.providerName')}><Input value={p.name || ''} onChange={(e) => setProvider(i, { name: e.target.value })} /></Field>
                  <Field label={t('schedcfg.staffId')}>{renderStaffSelect(p.staff_id, (v) => setProvider(i, { staff_id: v }))}</Field>
                  <Field label={t('schedcfg.resourceType')}><Input value={p.resource_type || ''} onChange={(e) => setProvider(i, { resource_type: e.target.value })} /></Field>
                  <Field label={t('schedcfg.resourceId')}><Input value={p.resource_id || ''} onChange={(e) => setProvider(i, { resource_id: e.target.value })} /></Field>
                  <div className="md:col-span-2">
                    <Field label={t('schedcfg.providerTypes')}>
                      <Input
                        value={(p.appointment_type_ids || []).join(', ')}
                        onChange={(e) => setProvider(i, { appointment_type_ids: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                      />
                    </Field>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <Button size="sm" variant="ghost" className="text-red-500 ml-auto" onClick={() => removeProvider(i)}><Trash2 className="h-4 w-4 mr-1" />{t('schedcfg.remove')}</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Blackouts */}
      <Section title={t('schedcfg.blackouts')} action={<Button size="sm" variant="outline" onClick={addBlackout}><Plus className="h-4 w-4 mr-1" />{t('schedcfg.addBlackout')}</Button>}>
        {(cfg.blackouts || []).length === 0 ? <p className="text-xs text-gray-400">{t('common.noData')}</p> : (
          <div className="space-y-3">
            {cfg.blackouts.map((b, i) => (
              <div key={i} className="border border-gray-100 rounded-md p-3 space-y-3">
                {/* Each field gets its OWN grid cell. The previous single 4-column row packed a date
                    AND a time into one cell, so Start and End ran into each other at ordinary desktop
                    widths - the defect the operator screenshot showed. Wraps to fewer columns on
                    narrow viewports and never overflows horizontally. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2 items-end">
                  <Field label={t('schedcfg.start') + ' - date'}>
                    <LocalDatePick value={b.start_local} testid={`blackout-start-date-${i}`}
                                   onChange={(local) => setBlackout(i, { start_local: local, start_ts: null })} />
                  </Field>
                  <Field label={t('schedcfg.start') + ' - time'}>
                    <LocalTimePick value={b.start_local} step={cfg.slot_granularity_minutes}
                                   testid={`blackout-start-time-${i}`}
                                   onChange={(local) => setBlackout(i, { start_local: local, start_ts: null })} />
                  </Field>
                  <Field label={t('schedcfg.end') + ' - date'}>
                    <LocalDatePick value={b.end_local} testid={`blackout-end-date-${i}`}
                                   onChange={(local) => setBlackout(i, { end_local: local, end_ts: null })} />
                  </Field>
                  <Field label={t('schedcfg.end') + ' - time'}>
                    <LocalTimePick value={b.end_local} step={cfg.slot_granularity_minutes}
                                   testid={`blackout-end-time-${i}`}
                                   onChange={(local) => setBlackout(i, { end_local: local, end_ts: null })} />
                  </Field>
                </div>

                {blackoutError(b) ? (
                  <p data-testid={`blackout-error-${i}`} className="text-xs text-red-600">{blackoutError(b)}</p>
                ) : null}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2 items-end">
                  <div className="lg:col-span-2">
                    <Field label={t('schedcfg.reason')}>
                      <Input value={b.reason || ''} onChange={(e) => setBlackout(i, { reason: e.target.value })} />
                    </Field>
                  </div>
                  <Field label={t('schedcfg.providerOptional')}>{renderStaffSelect(b.provider_staff_id, (v) => setBlackout(i, { provider_staff_id: v }))}</Field>
                  <div className="flex items-end">
                    <Button size="sm" variant="ghost" className="text-red-500 h-9" onClick={() => removeBlackout(i)}>
                      <Trash2 className="h-4 w-4 mr-1" />Remove
                    </Button>
                  </div>
                </div>

                {/* F5 — recurrence, inside the SAME row and the SAME authority (SchedulingConfig.blackouts).
                    "Does not repeat" is the default and writes repeat=null, which is byte-identical to
                    every blackout stored before F5. Raw rule syntax is never exposed. */}
                <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-gray-100">
                  <Field label={t('schedcfg.repeat') || 'Repeat'}>
                    <select
                      data-testid={`blackout-repeat-${i}`}
                      className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
                      value={b.repeat || ''}
                      onChange={(e) => {
                        const v = e.target.value || null;
                        setBlackout(i, v === null
                          ? { repeat: null, repeat_weekdays: [], repeat_until: null }
                          : { repeat: v, repeat_weekdays: v === 'weekly' ? (b.repeat_weekdays || []) : [] });
                      }}
                    >
                      <option value="">Does not repeat</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </select>
                  </Field>

                  {b.repeat === 'weekly' && (
                    <Field label={t('schedcfg.repeatOn') || 'On'}>
                      <div className="flex gap-1" data-testid={`blackout-weekdays-${i}`}>
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((lbl, wd) => {
                          const on = (b.repeat_weekdays || []).includes(wd);
                          return (
                            <button
                              key={wd}
                              type="button"
                              data-testid={`blackout-weekday-${i}-${wd}`}
                              aria-pressed={on}
                              className={`h-9 w-10 rounded-md border text-xs ${on ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-200'}`}
                              onClick={() => {
                                const cur = new Set(b.repeat_weekdays || []);
                                if (cur.has(wd)) cur.delete(wd); else cur.add(wd);
                                setBlackout(i, { repeat_weekdays: [...cur].sort((x, y) => x - y) });
                              }}
                            >{lbl}</button>
                          );
                        })}
                      </div>
                    </Field>
                  )}

                  {b.repeat && (
                    <Field label={t('schedcfg.repeatUntil') || 'Until'}>
                      <Input
                        type="date"
                        data-testid={`blackout-until-${i}`}
                        value={(b.repeat_until || '').slice(0, 10)}
                        onChange={(e) => setBlackout(i, { repeat_until: e.target.value || null })}
                      />
                    </Field>
                  )}

                  {b.repeat && recurrenceError(b) ? (
                    <p data-testid={`blackout-recurrence-error-${i}`} className="text-xs text-red-600 self-center">{recurrenceError(b)}</p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Policies */}
      <Section title={t('schedcfg.policies')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-gray-100 rounded-md p-3">
            <div className="text-sm font-medium mb-2">{t('sched.policy.cancellation')}</div>
            <Chk label={t('sched.policy.allowCustomerCancel')} v={cfg.cancellation_policy.allow_customer_cancel} on={(v) => set({ cancellation_policy: { ...cfg.cancellation_policy, allow_customer_cancel: v } })} />
            <Field label={t('sched.policy.minNotice')}><Input type="number" value={cfg.cancellation_policy.min_notice_minutes} onChange={(e) => set({ cancellation_policy: { ...cfg.cancellation_policy, min_notice_minutes: e.target.value } })} /></Field>
            <div className="mt-2"><Chk label={t('schedcfg.feeApplies')} v={cfg.cancellation_policy.fee_applies} on={(v) => set({ cancellation_policy: { ...cfg.cancellation_policy, fee_applies: v } })} /></div>
          </div>
          <div className="border border-gray-100 rounded-md p-3">
            <div className="text-sm font-medium mb-2">{t('sched.policy.reschedule')}</div>
            <Chk label={t('sched.policy.allowCustomerReschedule')} v={cfg.reschedule_policy.allow_customer_reschedule} on={(v) => set({ reschedule_policy: { ...cfg.reschedule_policy, allow_customer_reschedule: v } })} />
            <Field label={t('sched.policy.minNotice')}><Input type="number" value={cfg.reschedule_policy.min_notice_minutes} onChange={(e) => set({ reschedule_policy: { ...cfg.reschedule_policy, min_notice_minutes: e.target.value } })} /></Field>
            <Field label={t('sched.policy.maxReschedules')}><Input type="number" value={cfg.reschedule_policy.max_reschedules == null ? '' : cfg.reschedule_policy.max_reschedules} onChange={(e) => set({ reschedule_policy: { ...cfg.reschedule_policy, max_reschedules: e.target.value } })} /></Field>
          </div>
          <div className="border border-gray-100 rounded-md p-3">
            <div className="text-sm font-medium mb-2">{t('sched.policy.confirmation')}</div>
            <Field label={t('schedcfg.confirmationMode')}><Input value={cfg.confirmation_policy.mode || ''} onChange={(e) => set({ confirmation_policy: { ...cfg.confirmation_policy, mode: e.target.value } })} /></Field>
            <Field label={t('sched.policy.expiresAfter')}><Input type="number" value={cfg.confirmation_policy.expires_after_minutes == null ? '' : cfg.confirmation_policy.expires_after_minutes} onChange={(e) => set({ confirmation_policy: { ...cfg.confirmation_policy, expires_after_minutes: e.target.value } })} /></Field>
          </div>
        </div>
        <div className="mt-3">
          <Field label={t('schedcfg.knowledgeRefsInput')}>
            <Input
              value={(cfg.policy_knowledge_refs || []).join(', ')}
              onChange={(e) => set({ policy_knowledge_refs: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
            />
          </Field>
          <p className="text-[11px] text-gray-400 mt-1">{t('sched.policy.knowledgeRefsHint')}</p>
        </div>
      </Section>

      <div className="flex justify-end pb-8">
        <Button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {t('schedcfg.save')}
        </Button>
      </div>
    </div>
  );
};

//: What the server falls back to when a type names no kinds. Mirrored here ONLY so an unset type
//: renders its real effective state rather than an empty pair of unticked boxes; the server remains
//: the authority and re-derives this itself.
const DEFAULT_DOC_KINDS = ['document', 'image'];

/** The kinds after toggling one on or off. Order is stable so saving twice is a no-op. */
const toggleKind = (tp, kind, on) => {
  const current = tp.document_media_kinds || DEFAULT_DOC_KINDS;
  const next = on ? [...new Set([...current, kind])] : current.filter((k) => k !== kind);
  return DEFAULT_DOC_KINDS.filter((k) => next.includes(k));
};

const Txt = ({ label, v, on, placeholder }) => (
  <label className="block">
    <span className="text-xs text-gray-500">{label}</span>
    <Input value={v} placeholder={placeholder} onChange={(e) => on(e.target.value)} className="mt-1" />
  </label>
);

const Chk = ({ label, v, on }) => (
  <label className="flex items-center gap-2 text-sm text-gray-700">
    <input type="checkbox" checked={!!v} onChange={(e) => on(e.target.checked)} />
    {label}
  </label>
);

export default SchedulingConfig;

// Exported for the regression suites ONLY, so they pin the validators the component ACTUALLY runs
// rather than a copy that can drift away from them unnoticed. Nothing else imports these.
export { blackoutError, recurrenceError };
